import type { ReactNode } from 'react';
import { AlertTriangle, type LucideIcon } from 'lucide-react';
import { useResourceKit } from '@resource-kit/core/react';
import { cn } from '../../cn';

interface ErrorStateProps {
  /** Icon; defaults to a warning triangle. */
  icon?: LucideIcon;
  /** Optional headline; falls back to a generic localized title. */
  title?: string;
  /** The error detail — already localized by the caller. */
  message: string;
  /** Optional recovery action (e.g. a "Retry" button). */
  action?: ReactNode;
  className?: string;
}

/**
 * Canonical error-state primitive: warning icon + title + message and
 * optional recovery action, centered with destructive accents.
 * Replaces the assorted inline `text-destructive` blocks so a failed
 * load reads the same on every list/card page (#1290).
 *
 * Renders no Card wrapper — callers already inside a `<Card>` keep it.
 */
export function ErrorState({
  icon: Icon = AlertTriangle,
  title,
  message,
  action,
  className,
}: ErrorStateProps) {
  const { t } = useResourceKit();
  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center justify-center gap-3 py-12 text-center', className)}
    >
      <Icon className="w-10 h-10 text-destructive" aria-hidden="true" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-destructive">{title ?? t('rk.ui.error')}</p>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
